import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import LiveRoomCard from '../components/LiveRoomCard';
import { Search, Radio, Plus } from 'lucide-react';
import axios from 'axios';
import { tagOptions } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rooms`);
      setRooms(res.data);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRooms();
    const interval = setInterval(fetchRooms, 10000);
    return () => clearInterval(interval);
  }, [fetchRooms]);

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  const filteredRooms = rooms.filter(r => {
    const matchSearch = !searchQuery || r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.host_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchTag = activeTag === 'all' || r.tags?.includes(activeTag);
    return matchSearch && matchTag;
  });

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050108]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl font-bold flex items-center gap-3">
              <Radio className="w-6 h-6 text-[#FF007F]" />
              {t('liveNow')}
            </h1>
          </div>
          <button data-testid="go-live-btn" onClick={() => navigate('/create-room')}
            className="gradient-button rounded-full px-6 py-3 font-semibold flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Create Room
          </button>
        </div>

        {/* Search + Tags */}
        <div className="mb-8 space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input data-testid="search-input" type="text" placeholder={t('search')} value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-[#FF007F]/50 transition-colors" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setActiveTag('all')}
              className={`tag-pill ${activeTag === 'all' ? 'active' : ''}`}>
              All
            </button>
            {tagOptions.map(tag => (
              <button key={tag} onClick={() => setActiveTag(tag)}
                className={`tag-pill ${activeTag === tag ? 'active' : ''}`}>
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Room Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="glass-card rounded-3xl h-72 animate-pulse" />
            ))}
          </div>
        ) : filteredRooms.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredRooms.map(room => (
              <LiveRoomCard key={room.id} room={room} onClick={() => navigate(`/room/${room.id}`, { state: { title: room.title, tags: room.tags } })} />
            ))}
          </div>
        ) : (
          <div data-testid="no-rooms" className="text-center py-20">
            <Radio className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg">{t('noRooms')}</p>
            <button data-testid="go-live-empty-btn" onClick={() => navigate('/create-room')}
              className="mt-6 gradient-button rounded-full px-8 py-3 font-semibold">
              Create Room
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
